import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

const DEFAULT_POSITION = 50;

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function normalizePosition(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return DEFAULT_POSITION;
  }
  return clamp(parsed, 0, 100);
}

export default function GroupCoverPositioner({
  imageSrc,
  alt,
  positionX = DEFAULT_POSITION,
  positionY = DEFAULT_POSITION,
  scale = 1,
  hintLabel = "",
  onPositionChange,
}) {
  const { t } = useTranslation();
  const frameRef = useRef(null);
  const imgRef = useRef(null);
  const dragRef = useRef({
    active: false,
    pointerId: null,
    startX: 0,
    startY: 0,
    baseOffsetX: 0,
    baseOffsetY: 0,
  });
  const lastPositionRef = useRef({
    x: normalizePosition(positionX),
    y: normalizePosition(positionY),
  });

  const [naturalSize, setNaturalSize] = useState({ width: 0, height: 0 });
  const [frameSize, setFrameSize] = useState({ width: 0, height: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [zoom, setZoom] = useState(scale);
  const [localPosition, setLocalPosition] = useState({
    x: normalizePosition(positionX),
    y: normalizePosition(positionY),
  });

  useEffect(() => {
    const nextPosition = {
      x: normalizePosition(positionX),
      y: normalizePosition(positionY),
    };
    lastPositionRef.current = nextPosition;
    setLocalPosition(nextPosition);
    setZoom(scale);
  }, [imageSrc, positionX, positionY, scale]);

  useEffect(() => {
    setNaturalSize({ width: 0, height: 0 });
  }, [imageSrc]);

  useEffect(() => {
    function measureFrame() {
      const rect = frameRef.current?.getBoundingClientRect();
      if (!rect) return;
      setFrameSize({
        width: rect.width,
        height: rect.height,
      });
    }

    measureFrame();

    if (!frameRef.current) return undefined;

    let observer;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(measureFrame);
      observer.observe(frameRef.current);
    } else {
      window.addEventListener("resize", measureFrame);
    }

    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", measureFrame);
    };
  }, []);

  const baseCoverScale = useMemo(() => {
    if (!frameSize.width || !frameSize.height || !naturalSize.width || !naturalSize.height) {
      return 1;
    }

    return Math.max(
      frameSize.width / naturalSize.width,
      frameSize.height / naturalSize.height
    );
  }, [frameSize.height, frameSize.width, naturalSize.height, naturalSize.width]);

  const coverScale = baseCoverScale * zoom;

  const maxOffsets = useMemo(() => {
    const imageWidth = naturalSize.width * coverScale;
    const imageHeight = naturalSize.height * coverScale;

    return {
      x: Math.max(0, (imageWidth - frameSize.width) / 2),
      y: Math.max(0, (imageHeight - frameSize.height) / 2),
    };
  }, [coverScale, frameSize.height, frameSize.width, naturalSize.height, naturalSize.width]);

  const offset = useMemo(() => {
    return {
      x: maxOffsets.x ? ((localPosition.x - DEFAULT_POSITION) / DEFAULT_POSITION) * maxOffsets.x : 0,
      y: maxOffsets.y ? ((localPosition.y - DEFAULT_POSITION) / DEFAULT_POSITION) * maxOffsets.y : 0,
    };
  }, [localPosition.x, localPosition.y, maxOffsets.x, maxOffsets.y]);

  function offsetToPosition(nextOffsetX, nextOffsetY) {
    return {
      x: maxOffsets.x
        ? clamp(
            DEFAULT_POSITION + (nextOffsetX / maxOffsets.x) * DEFAULT_POSITION,
            0,
            100
          )
        : DEFAULT_POSITION,
      y: maxOffsets.y
        ? clamp(
            DEFAULT_POSITION + (nextOffsetY / maxOffsets.y) * DEFAULT_POSITION,
            0,
            100
          )
        : DEFAULT_POSITION,
    };
  }

  function clampOffsets(nextOffsetX, nextOffsetY) {
    return {
      x: clamp(nextOffsetX, -maxOffsets.x, maxOffsets.x),
      y: clamp(nextOffsetY, -maxOffsets.y, maxOffsets.y),
    };
  }

  function updateLocalPosition(nextPosition, shouldCommit = false) {
    lastPositionRef.current = nextPosition;
    setLocalPosition(nextPosition);

    if (shouldCommit && onPositionChange) {
      onPositionChange({
        x: Number(nextPosition.x.toFixed(2)),
        y: Number(nextPosition.y.toFixed(2)),
        scale: zoom,
      });
    }
  }

  function finishDragging() {
    if (!dragRef.current.active) return;

    dragRef.current.active = false;
    dragRef.current.pointerId = null;
    setIsDragging(false);
    updateLocalPosition(lastPositionRef.current, true);
  }

  function handlePointerDown(event) {
    if (!imageSrc || !naturalSize.width || !naturalSize.height) {
      return;
    }

    event.preventDefault();
    dragRef.current = {
      active: true,
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      baseOffsetX: offset.x,
      baseOffsetY: offset.y,
    };
    setIsDragging(true);
    event.currentTarget.setPointerCapture?.(event.pointerId);
  }

  function handlePointerMove(event) {
    if (!dragRef.current.active || dragRef.current.pointerId !== event.pointerId) {
      return;
    }

    const deltaX = event.clientX - dragRef.current.startX;
    const deltaY = event.clientY - dragRef.current.startY;
    const nextOffsets = clampOffsets(
      dragRef.current.baseOffsetX + deltaX,
      dragRef.current.baseOffsetY + deltaY
    );

    updateLocalPosition(offsetToPosition(nextOffsets.x, nextOffsets.y));
  }

  function handlePointerUp(event) {
    if (dragRef.current.pointerId !== event.pointerId) {
      return;
    }

    event.currentTarget.releasePointerCapture?.(event.pointerId);
    finishDragging();
  }

  function handleImageLoad() {
    const img = imgRef.current;
    if (!img) return;

    setNaturalSize({
      width: img.naturalWidth,
      height: img.naturalHeight,
    });
  }

  return (
    <div className="groups-cover-positioner">
      <div
        ref={frameRef}
        className={`groups-cover-positioner-frame${isDragging ? " is-dragging" : ""}`}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={finishDragging}
      >
        {imageSrc ? (
          <img
            ref={imgRef}
            src={imageSrc}
            alt={alt}
            className="groups-cover-positioner-image"
            onLoad={handleImageLoad}
            draggable={false}
            style={{
              transform: `translate(-50%, -50%) translate(${offset.x}px, ${offset.y}px) scale(${coverScale})`,
            }}
          />
        ) : null}
      </div>

      <div className="groups-cover-positioner-zoom">
        <label htmlFor="group-cover-zoom-slider">
          {t("zoom")}
        </label>
        <input
          id="group-cover-zoom-slider"
          type="range"
          min="1"
          max="3"
          step="0.05"
          value={zoom}
          onChange={(e) => setZoom(Number(e.target.value))}
          className="groups-cover-zoom-slider"
        />
      </div>

      {hintLabel ? (
        <p className="groups-cover-positioner-hint">{hintLabel}</p>
      ) : null}
    </div>
  );
}
